import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import Validator from 'validatorjs';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import {
  Form, Button, Modal, Header, Icon, Message, Radio, Select,
} from 'semantic-ui-react';

import { formatDateOnly } from '../../utilities/dateUtils';
import {
  MASTER_TYPE,
  PARTY_SIDE_LABEL,
  CREATE_BLAST_RADIUS,
  NO_ELIGIBLE_PRODUCTS_GUIDANCE,
  NOT_CONFIGURED_FOR_SIDE_GUIDANCE,
  STALE_VERSION_HEADER,
  STALE_VERSION_GUIDANCE,
} from '../../constants/defaultCoverageMaster';
import PreferenceRulesEditor from './PreferenceRulesEditor';

// A price is REQUIRED, not a nicety: with no valid price on either the
// subscription or its group, findActivePrice throws NO_ACTIVE_SUBSCRIPTION_PRICE
// on the SALE path — a master created without one looks fine here and blows up on
// a real sale.
export const PRICE_REQUIRED_ERROR = 'Price is required — a master with no price fails on the sale, not here.';
export const PRICE_FORMAT_ERROR = 'Enter a price greater than zero, up to two decimal places.';

const PRICE_PATTERN = /^\d{1,4}(\.\d{1,2})?$/;

export const priceFormatError = price => {
  const value = String(price === null || price === undefined ? '' : price).trim();
  if (isEmpty(value)) return null;
  if (!PRICE_PATTERN.test(value)) return PRICE_FORMAT_ERROR;
  return Number(value) > 0 ? null : PRICE_FORMAT_ERROR;
};

const validationRules = {
  masterType: 'required',
  productId: 'required',
  priceDs: 'required',
};

const DefaultCoverageMasterForm = ({
  isModalOpen,
  onCloseHandler,
  onSubmitHandler,
  onRefreshHandler,
  onMasterTypeChange,
  error,
  staleVersionError,
  submitting,
  action,
  productOptions,
  productsLoading,
  affectedOptedOutParties,
  hasAnyMasterOnSide,
  defaultCoverageMaster,
  createdMaster,
}) => {
  const isEdit = action === 'edit';
  // Once create succeeds, createdMaster carries the real row — same shape Edit
  // already works with — so the rules step below can treat "just created" and
  // "editing an existing master" identically: both key off a real subscription id.
  const rulesSubscriptionId = isEdit
    ? (defaultCoverageMaster && defaultCoverageMaster.id)
    : (createdMaster && createdMaster.id);
  const rulesPreferenceType = isEdit
    ? (defaultCoverageMaster && defaultCoverageMaster.masterType)
    : (createdMaster && createdMaster.masterType);
  const showingRulesStep = !isEdit && Boolean(createdMaster);

  const [id, setId] = useState(null);
  // Not user-editable: the row version this form was loaded with, held only so
  // submit can hand it back for the optimistic-lock check.
  const [version, setVersion] = useState(null);
  const [masterType, setMasterType] = useState(MASTER_TYPE.BUYER);
  const [productId, setProductId] = useState('');
  const [priceDs, setPriceDs] = useState('');
  const [startTs, setStartTs] = useState(null);
  const [endTs, setEndTs] = useState(null);
  const [errors, setErrors] = useState({});
  // Rules save on their own (Save Rules), separately from Create/Update. Without this, a
  // close or an Update would drop rule edits nobody saved, with nothing on screen to say
  // so. The pending action is what the operator was trying to do when they were stopped,
  // so Discard can carry it out; the editor key remounts the editor, which reloads the
  // stored rules — that is what actually throws the edits away.
  const [rulesDirty, setRulesDirty] = useState(false);
  const [unsavedRulesAction, setUnsavedRulesAction] = useState(null);
  const [rulesEditorKey, setRulesEditorKey] = useState(0);

  useEffect(() => {
    if (!isEmpty(defaultCoverageMaster)) {
      setId(defaultCoverageMaster.id);
      setVersion(
        defaultCoverageMaster.version !== null && defaultCoverageMaster.version !== undefined
          ? defaultCoverageMaster.version
          : null
      );
      setMasterType(defaultCoverageMaster.masterType || MASTER_TYPE.BUYER);
      setProductId(defaultCoverageMaster.productId || '');
      setPriceDs(
        defaultCoverageMaster.priceDs === null || defaultCoverageMaster.priceDs === undefined
          ? ''
          : String(defaultCoverageMaster.priceDs)
      );
      setStartTs(defaultCoverageMaster.startTs ? moment(defaultCoverageMaster.startTs) : null);
      setEndTs(defaultCoverageMaster.endTs ? moment(defaultCoverageMaster.endTs) : null);
    }
  }, [defaultCoverageMaster]);

  // Once the rules are saved (or the form closes some other way) there is nothing left
  // to warn about.
  useEffect(() => {
    if (!rulesDirty || !isModalOpen) setUnsavedRulesAction(null);
  }, [rulesDirty, isModalOpen]);

  const noEligibleProducts = !isEdit && !productsLoading && isEmpty(productOptions);
  const readOnlyClassName = isEdit ? 'input-disabled' : '';

  // Same presence checks submitForm runs before sending the request, hoisted here so
  // Create master is not clickable at all until they are satisfied - previously they
  // only fired an inline error AFTER a click that visibly did nothing. Presence only
  // (not format): a malformed price is still "present" and stays a submit-time error,
  // caught by priceFormatError.
  const noProductSelected = !isEdit && isEmpty(String(productId));
  const noPriceEntered = !isEdit && isEmpty(String(priceDs).trim());

  const onChangeHandler = (setter, field) => event => {
    setter(event.target.value);
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  // Switching sides re-asks the server which products are eligible, because the
  // answer differs per side: one product can carry a buyer master and a seller
  // master at once. The chosen product is cleared, since it may not be offerable
  // on the new side.
  const onMasterTypeChangeHandler = side => () => {
    setMasterType(side);
    setProductId('');
    setErrors(prev => ({ ...prev, masterType: undefined, productId: undefined }));
    if (onMasterTypeChange) onMasterTypeChange(side);
  };

  const onProductChangeHandler = (event, { value }) => {
    setProductId(value);
    setErrors(prev => ({ ...prev, productId: undefined }));
  };

  const submitForm = () => {
    if (noEligibleProducts) return;

    const formState = {
      masterType,
      productId,
      priceDs: String(priceDs).trim(),
    };
    const validation = new Validator(formState, validationRules);
    const validationFailed = validation.fails();

    const fieldErrors = validationFailed ? validation.errors.all() : {};
    const priceError = priceFormatError(priceDs);
    if (priceError) fieldErrors.priceDs = [priceError];

    setErrors(fieldErrors);
    if (validationFailed || priceError) return;

    onSubmitHandler({
      id,
      version,
      masterType,
      productId,
      priceDs: Number(String(priceDs).trim()),
      startTs: startTs ? formatDateOnly(startTs) : null,
      endTs: endTs ? formatDateOnly(endTs) : null,
    });
  };

  const requestClose = () => {
    if (rulesDirty) {
      setUnsavedRulesAction('close');
      return;
    }
    onCloseHandler();
  };

  // Update sends price and dates only; on success the container closes the form.
  const requestUpdate = () => {
    if (rulesDirty) {
      setUnsavedRulesAction('update');
      return;
    }
    submitForm();
  };

  const discardRulesAndContinue = () => {
    const pendingAction = unsavedRulesAction;
    setUnsavedRulesAction(null);
    setRulesDirty(false);
    if (pendingAction === 'close') {
      onCloseHandler();
      return;
    }
    setRulesEditorKey(key => key + 1);
    submitForm();
  };

  return (
    <Modal
      data-testid='default-coverage-master-form-modal'
      open={isModalOpen}
      closeIcon
      onClose={requestClose}
      closeOnEscape={false}
      closeOnRootNodeClick={false}
    >
      <Modal.Header data-testid='default-coverage-master-form-title'>
        {!isEdit && !showingRulesStep && 'Add Default Coverage Master'}
        {showingRulesStep && 'Review Rules'}
        {isEdit && 'Edit Default Coverage Master'}
      </Modal.Header>
      <Modal.Content image>
        <Modal.Description>
          {error && (
            <Message negative>
              <p>{error}</p>
            </Message>
          )}
          {staleVersionError && (
            <Message negative icon data-testid='default-coverage-master-stale-version-error'>
              <Icon name='warning sign' />
              <Message.Content>
                <Message.Header>{STALE_VERSION_HEADER}</Message.Header>
                <p>{staleVersionError}</p>
                <p>{STALE_VERSION_GUIDANCE}</p>
              </Message.Content>
            </Message>
          )}

          {/* This screen CREATES charges, unlike the opt-out screen which only
              removes them. The blast radius is stated before the operator acts,
              not discovered afterwards in an invoice. */}
          {!isEdit && !showingRulesStep && (
            <Message warning icon data-testid='default-coverage-master-blast-radius'>
              <Icon name='exclamation triangle' />
              <Message.Content>
                <p>{CREATE_BLAST_RADIUS}</p>
                {/* Adding a master changes what EXISTING opt-out rows do: a party
                    opted out of every current master on this side falls through to
                    the new one and starts being charged again, without anybody
                    editing their row. */}
                {affectedOptedOutParties > 0 && (
                  <p data-testid='default-coverage-master-affected-parties'>
                    <b>
                      {affectedOptedOutParties}
                      {affectedOptedOutParties === 1 ? ' party is' : ' parties are'}
                    </b>
                    {' '}
                    currently opted out of every master on this side. Adding this
                    master makes them eligible again unless they are also opted
                    out of this product.
                  </p>
                )}
              </Message.Content>
            </Message>
          )}

          {/* Two different problems, same color: neither has an action this
              screen's banner alone resolves without the operator doing
              something outside this form (end an existing master, switch
              sides, or wait on APMS config), so both read negative. */}
          {noEligibleProducts && !showingRulesStep && (
            <Message negative icon data-testid='default-coverage-master-no-eligible-products'>
              <Icon name='exclamation triangle' />
              <Message.Content>
                <p>
                  {hasAnyMasterOnSide
                    ? NO_ELIGIBLE_PRODUCTS_GUIDANCE
                    : NOT_CONFIGURED_FOR_SIDE_GUIDANCE}
                </p>
              </Message.Content>
            </Message>
          )}

          {showingRulesStep && (
            <Message data-testid='default-coverage-master-created-notice'>
              <p>
                Master created on product {createdMaster.productCode || createdMaster.productId}.
                It is already live under the standard template rules shown below. Any change
                you make here applies from the moment you save it — sales that happen before
                then are charged under the template.
              </p>
            </Message>
          )}

          {!showingRulesStep && (
          <Form>
            <Header size='small' color='blue' />
            <Form.Group widths='equal'>
              <Form.Field required error={!isEmpty(errors.masterType)}>
                <label htmlFor='masterTypeBuyer'>Payer Side</label>
                <Radio
                  id='masterTypeBuyer'
                  data-testid='default-coverage-master-side-buyer'
                  label={PARTY_SIDE_LABEL[MASTER_TYPE.BUYER]}
                  name='masterType'
                  value={MASTER_TYPE.BUYER}
                  checked={masterType === MASTER_TYPE.BUYER}
                  onChange={onMasterTypeChangeHandler(MASTER_TYPE.BUYER)}
                  disabled={isEdit}
                />
                <Radio
                  id='masterTypeSeller'
                  data-testid='default-coverage-master-side-seller'
                  label={PARTY_SIDE_LABEL[MASTER_TYPE.SELLER]}
                  name='masterType'
                  value={MASTER_TYPE.SELLER}
                  checked={masterType === MASTER_TYPE.SELLER}
                  onChange={onMasterTypeChangeHandler(MASTER_TYPE.SELLER)}
                  disabled={isEdit}
                  style={{ marginLeft: '1em' }}
                />
                <Radio
                  id='masterTypeSellerPaid'
                  data-testid='default-coverage-master-side-seller-paid'
                  label={PARTY_SIDE_LABEL[MASTER_TYPE.SELLER_PAID]}
                  name='masterType'
                  value={MASTER_TYPE.SELLER_PAID}
                  checked={masterType === MASTER_TYPE.SELLER_PAID}
                  onChange={onMasterTypeChangeHandler(MASTER_TYPE.SELLER_PAID)}
                  disabled={isEdit}
                  style={{ marginLeft: '1em' }}
                />
                {errors.masterType && <span>{errors.masterType}</span>}
              </Form.Field>

              {/* Immutable on edit: moving a master to another product would
                  silently change what every sale on that side is charged for,
                  with nothing on the row to show it happened. End it and create
                  a new one instead. */}
              {isEdit && (
                <Form.Field>
                  <label htmlFor='productCode'>Product</label>
                  <input
                    id='productCode'
                    name='productCode'
                    data-testid='default-coverage-master-product-readonly'
                    value={
                      defaultCoverageMaster
                        ? String(defaultCoverageMaster.productCode
                            || defaultCoverageMaster.productId || '')
                        : ''
                    }
                    readOnly
                    className={readOnlyClassName}
                  />
                </Form.Field>
              )}
              {!isEdit && !noEligibleProducts && (
                <Form.Field required error={!isEmpty(errors.productId)}>
                  <label htmlFor='productId'>Product</label>
                  <Select
                    id='productId'
                    name='productId'
                    data-testid='default-coverage-master-product-select'
                    fluid
                    placeholder='Select a product'
                    options={productOptions}
                    value={productId}
                    onChange={onProductChangeHandler}
                    loading={productsLoading}
                    disabled={productsLoading}
                    search
                    closeOnBlur
                    selectOnBlur={false}
                  />
                  {errors.productId && <span>{errors.productId}</span>}
                </Form.Field>
              )}
            </Form.Group>

            <Form.Group widths='equal'>
              <Form.Field required error={!isEmpty(errors.priceDs)}>
                <label htmlFor='priceDs'>Price (DS)</label>
                <input
                  id='priceDs'
                  name='priceDs'
                  data-testid='default-coverage-master-price-input'
                  value={priceDs}
                  onChange={onChangeHandler(setPriceDs, 'priceDs')}
                  inputMode='decimal'
                  maxLength={7}
                />
                {errors.priceDs && <span>{errors.priceDs}</span>}
              </Form.Field>
              <Form.Field>
                <label htmlFor='startTs'>Start date</label>
                <DatePicker
                  id='startTs'
                  customInput={(
                    <input
                      data-testid='default-coverage-master-start-input'
                      style={{ padding: '0', width: '100%' }}
                    />
                  )}
                  selected={startTs}
                  onChange={newDate => setStartTs(newDate ? moment(newDate) : null)}
                  dateFormat='YYYY-MM-DD'
                  placeholderText='Leave blank to start now'
                  isClearable
                />
              </Form.Field>
              <Form.Field>
                <label htmlFor='endTs'>End date</label>
                <DatePicker
                  id='endTs'
                  customInput={(
                    <input
                      data-testid='default-coverage-master-end-input'
                      style={{ padding: '0', width: '100%' }}
                    />
                  )}
                  selected={endTs}
                  onChange={newDate => setEndTs(newDate ? moment(newDate) : null)}
                  dateFormat='YYYY-MM-DD'
                  placeholderText='Leave blank to stay open-ended'
                  isClearable
                />
              </Form.Field>
            </Form.Group>
          </Form>
          )}

          {/* A master is a subscription under the hood, so its rules live in the
              same subscription_preferences row every dealer's own preferences do —
              this editor is keyed by the master's id either way, once one exists. */}
          {Boolean(rulesSubscriptionId) && (
            <PreferenceRulesEditor
              key={rulesEditorKey}
              subscriptionId={rulesSubscriptionId}
              preferenceType={rulesPreferenceType}
              onDirtyChange={setRulesDirty}
            />
          )}
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        {unsavedRulesAction && (
          <Message warning data-testid='default-coverage-master-unsaved-rules' style={{ textAlign: 'left' }}>
            <p>
              You have unsaved rule changes.
              {unsavedRulesAction === 'update'
                ? ' Update saves the price and dates only.'
                : ''}
              {' '}
              Use Save Rules above to keep them, or discard them.
            </p>
            <Button
              data-testid='default-coverage-master-unsaved-rules-keep'
              size='small'
              onClick={() => setUnsavedRulesAction(null)}
            >
              Keep editing
            </Button>
            <Button
              data-testid='default-coverage-master-unsaved-rules-discard'
              size='small'
              negative
              onClick={discardRulesAndContinue}
            >
              {unsavedRulesAction === 'update' ? 'Discard rule changes and update' : 'Discard rule changes and close'}
            </Button>
          </Message>
        )}
        {showingRulesStep && (
          <Button
            data-testid='default-coverage-master-form-done'
            color='green'
            onClick={requestClose}
            inverted
          >
            <Icon name='checkmark' /> Done
          </Button>
        )}
        {!showingRulesStep && submitting && (
          <Button loading positive>
            Submitting...
          </Button>
        )}
        {!showingRulesStep && !submitting && !staleVersionError && !id && (
          <Button
            data-testid='default-coverage-master-form-submit'
            color='green'
            onClick={submitForm}
            disabled={noEligibleProducts || noProductSelected || noPriceEntered}
            inverted
          >
            <Icon name='checkmark' /> Create master
          </Button>
        )}
        {!showingRulesStep && !submitting && !staleVersionError && id && (
          <Button
            data-testid='default-coverage-master-form-submit'
            color='green'
            onClick={requestUpdate}
            inverted
          >
            <Icon name='checkmark' /> Update
          </Button>
        )}
        {/* Nothing this form can send will succeed until the row is reloaded, so
            the only forward action offered is the refresh. */}
        {!showingRulesStep && !submitting && staleVersionError && onRefreshHandler && (
          <Button
            data-testid='default-coverage-master-form-refresh'
            color='blue'
            onClick={onRefreshHandler}
            inverted
          >
            <Icon name='refresh' /> Refresh
          </Button>
        )}
        {!showingRulesStep && (
          <Button data-testid='default-coverage-master-form-cancel' onClick={requestClose}>
            Cancel
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

DefaultCoverageMasterForm.propTypes = {
  isModalOpen: PropTypes.bool,
  onCloseHandler: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  onRefreshHandler: PropTypes.func,
  onMasterTypeChange: PropTypes.func,
  error: PropTypes.string,
  staleVersionError: PropTypes.string,
  submitting: PropTypes.bool,
  action: PropTypes.string,
  productOptions: PropTypes.array,
  productsLoading: PropTypes.bool,
  affectedOptedOutParties: PropTypes.number,
  hasAnyMasterOnSide: PropTypes.bool,
  defaultCoverageMaster: PropTypes.shape({
    id: PropTypes.string,
    version: PropTypes.number,
    masterType: PropTypes.string,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productCode: PropTypes.string,
    priceDs: PropTypes.number,
    startTs: PropTypes.string,
    endTs: PropTypes.string,
  }),
  createdMaster: PropTypes.shape({
    id: PropTypes.string,
    masterType: PropTypes.string,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productCode: PropTypes.string,
  }),
};

DefaultCoverageMasterForm.defaultProps = {
  isModalOpen: false,
  onRefreshHandler: null,
  onMasterTypeChange: null,
  error: null,
  staleVersionError: null,
  submitting: false,
  action: 'new',
  productOptions: [],
  productsLoading: false,
  affectedOptedOutParties: 0,
  hasAnyMasterOnSide: false,
  defaultCoverageMaster: null,
  createdMaster: null,
};

export default DefaultCoverageMasterForm;
